import { clientStatusOfColumn } from '@rundown/domain';
import { type BoardColumn } from '@rundown/types';
import { STATUS_LABELS } from '../requests/labels';
import type { NotificationRow } from './types';
import { isRecord } from '../../lib/guards';

/**
 * `kind` -> a plain-English sentence, e.g. "Imran replied" or "Murtaza
 * mentioned you". Modelled directly on `activity/formatActivity.ts`: `kind`
 * is an open set (see `notifications.ts`'s schema header), so every branch is
 * reached by matching a known string and the fallback humanises whatever it
 * does not recognise rather than throwing or dumping raw JSON.
 *
 * Deliberately does NOT say "on #12" — the request number and title are
 * rendered once, as the group header `NotificationBell.tsx` clusters these
 * lines under, not repeated on every line inside it.
 */

function actorOf(row: NotificationRow): string {
  return row.actorName ?? 'Someone';
}

function humanizeKind(kind: string): string {
  return kind.replace(/[._]/g, ' ');
}

export function formatNotification(row: NotificationRow): string {
  const actor = actorOf(row);
  const payload = isRecord(row.payload) ? row.payload : {};

  switch (row.kind) {
    case 'message.mentioned':
      return `${actor} mentioned you`;
    // Tagged in the words the request was raised with, rather than in a reply
    // — worth saying differently, because there is no message to go and read.
    case 'request.mentioned':
      return `${actor} tagged you when raising this`;
    case 'message.replied':
      return `${actor} replied`;
    case 'request.confirmed':
      return `${actor} confirmed this request`;
    case 'request.asked_client':
      return `${actor} asked for more detail`;
    case 'request.sprint_planned':
      return `${actor} planned this into a sprint`;
    case 'request.board_moved': {
      // `boardColumn` is internal vocabulary ('staging', 'triage') that never
      // appears in the client's five-state model — translated through the
      // same map the request DTO itself uses, exactly like
      // `activity/formatActivity.ts`'s `formatFieldValue` does for the same
      // field.
      const column =
        typeof payload['boardColumn'] === 'string' ? (payload['boardColumn'] as BoardColumn) : null;
      const status = column ? STATUS_LABELS[clientStatusOfColumn(column)] : null;
      return status ? `${actor} moved this to ${status}` : `${actor} moved this`;
    }
    case 'request.sprint_released':
      return `${actor} released this`;
    case 'request.sprint_locked': {
      const name = typeof payload['sprintName'] === 'string' ? payload['sprintName'] : 'the sprint';
      return `${name} started · this is locked in`;
    }
    case 'request.plan_change_raised':
      return `${actor} asked for a change of plan`;
    case 'request.plan_change_agreed':
      return `${actor} agreed a change of plan`;
    case 'request.plan_change_declined':
      return `${actor} declined a change of plan`;
    case 'request.followed_you_in':
      return `${actor} added you as a follower`;
    default:
      return `${actor} — ${humanizeKind(row.kind)}`;
  }
}
